// Pure quote-line pricing arithmetic for QuoteManagementPage.
//
// No React, no fetch: every function takes plain objects and returns plain
// objects so the page's "show the math" numbers can be reproduced (and unit
// tested) outside the component. All money as plain numbers; every helper
// tolerates null / undefined / '' / NaN and never throws.
//
// Vocabulary
//   line       a row of advancedQuoteLines (part, consumable "component", or
//              per-set "assembly ops" line)
//   breakdown  the calculator response stored on line.cost_breakdown
//   header     {target_margin, material_markup} as FRACTIONS (0.40, 0.20)
//   basis      the fraction that produced a price (target margin or markup)

import { num, approxEq, marginPct, priceFromMargin, priceFromMarkup, fmtMoney } from './priceMath';
import { QUOTE_SELL_RATE_FIELDS } from './quoteRateDefaults';

export const PRICE_SOURCES = ['calculated', 'target_margin', 'book', 'manual', 'markup', 'override_member', 'ops'];

/** The six sell-rate fields — ONE list, shared with quoteRateDefaults (decision 2, wave 2). */
export const SELL_RATE_FIELDS = QUOTE_SELL_RATE_FIELDS;

export const round2 = (v) => Math.round(num(v) * 100) / 100;

const firstFinite = (...vals) => {
  for (const v of vals) {
    const n = num(v, NaN);
    if (Number.isFinite(n)) return n;
  }
  return NaN;
};

export function normalizePriceSource(s, fallback = 'manual') {
  return PRICE_SOURCES.includes(s) ? s : fallback;
}

/**
 * quoteSetup holds percents (40, 20); the engine and the API use fractions.
 * opts.quotePricingMode 'tm' (a Time & Materials quote) adds quote_pricing_mode: 'tm', which makes
 * every basis helper below short-circuit to 'tm_pending' (priced at completion, never derived).
 * A fixed quote gets exactly the two keys it always did.
 */
export function headerFractions(setup, opts) {
  const tm = num(setup && setup.target_margin, NaN);
  const mm = num(setup && setup.material_markup, NaN);
  const out = {
    target_margin: Number.isFinite(tm) ? tm / 100 : null,
    material_markup: Number.isFinite(mm) ? mm / 100 : null,
  };
  if (opts && opts.quotePricingMode === 'tm') out.quote_pricing_mode = 'tm';
  return out;
}

/**
 * Time & Materials short-circuit: the LINE says tm_pending, or the HEADER is a T&M quote. Never
 * keyed on line.pricing_mode — on a component line that already means book / markup / margin.
 */
export function isTmPricedLine(line, header) {
  return !!((line && line.price_source === 'tm_pending') || (header && header.quote_pricing_mode === 'tm'));
}

const TM_REASON = 'T&M: priced at completion';

/** Sell rates that are 0 / blank on the header (F42). Returns [] when all are set. */
export function zeroSellRates(setup) {
  if (!setup) return [];
  return SELL_RATE_FIELDS
    .filter(([field]) => !(num(setup[field], 0) > 0))
    .map(([, label]) => label);
}

/** The basis a line prices by, with legacy-field fallbacks. */
export function effectivePriceSource(line, header) {
  if (!line) return 'manual';
  if (isTmPricedLine(line, header)) return 'tm_pending';
  if (PRICE_SOURCES.includes(line.price_source)) return line.price_source;
  if (line.is_assembly_ops) return 'calculated';
  if (line.is_consumable_view) {
    if (line.pricing_mode === 'book') return 'book';
    if (line.pricing_mode === 'markup') return 'markup';
    if (line.pricing_mode === 'margin') return 'target_margin';
    return 'manual';
  }
  return 'manual';
}

/** Per-unit COST of a line. NaN when unknown (no breakdown, unresolved consumable). */
export function lineUnitCost(line, breakdown) {
  const bd = breakdown || (line && line.cost_breakdown) || null;
  if (materialUnresolved(line, bd).length) return NaN;   // material unknown: the cost is not known
  const t = num(bd && bd.total_cost, NaN);
  if (Number.isFinite(t)) return t;
  if (line && line.is_consumable_view) {
    if (line._cost_resolved === false) return NaN;
    return num(line._cost, NaN);
  }
  return NaN;
}

/** Per-unit CALCULATED price of a line (engine price, or cost × (1+markup) for a component). */
export function lineCalculatedPrice(line, breakdown, header) {
  const bd = breakdown || (line && line.cost_breakdown) || null;
  if (materialUnresolved(line, bd).length) return NaN;   // priced without its material
  const c = num(bd && bd.calculated_price, NaN);
  if (Number.isFinite(c)) return c;
  if (line && line.is_consumable_view) {
    const cost = lineUnitCost(line, bd);
    const k = num(header && header.material_markup, NaN);
    if (Number.isFinite(cost) && Number.isFinite(k)) return priceFromMarkup(cost, k);
  }
  return NaN;
}

/**
 * Re-derive a line's price from its basis and a (fresh) breakdown.
 *   'calculated'    -> breakdown.calculated_price
 *   'target_margin' -> total_cost / (1 - basis)     basis = line.price_basis ?? header.target_margin
 *   'markup'        -> cost × (1 + basis)           basis = line.price_basis ?? header.material_markup
 *   'manual'|'book'|'override_member' -> unchanged
 * Returns {price, changed, reason, source, basis}; price is rounded to cents.
 */
export function deriveLinePrice(line, breakdown, header) {
  const current = num(line && line.price, NaN);
  if (isTmPricedLine(line, header)) {
    return { price: 0, changed: Number.isFinite(current) && !approxEq(current, 0), reason: TM_REASON, source: 'tm_pending', basis: null };
  }
  const source = effectivePriceSource(line);
  const bd = breakdown || (line && line.cost_breakdown) || null;
  const h = header || {};
  const keep = (reason) => ({
    price: Number.isFinite(current) ? current : null, changed: false, reason, source, basis: null,
  });

  const unresolved = materialUnresolved(line, bd);
  if (unresolved.length && ['calculated', 'target_margin', 'markup'].includes(source)) {
    return keep(`material cost unresolved: ${unresolved[0].message || unresolved[0].code}`);
  }
  if (source === 'calculated') {
    const calc = num(bd && bd.calculated_price, NaN);
    if (!Number.isFinite(calc)) return keep('no calculated price in breakdown');
    const price = round2(calc);
    return { price, changed: !approxEq(price, current), reason: `calculated price ${fmtMoney(calc)}`, source, basis: null };
  }
  if (source === 'target_margin') {
    const cost = lineUnitCost(line, bd);
    const lineBasis = num(line && line.price_basis, NaN);
    const m = Number.isFinite(lineBasis) ? lineBasis : num(h.target_margin, NaN);
    if (!Number.isFinite(cost)) return keep('no cost to apply the target margin to');
    if (!Number.isFinite(m) || m >= 1) return keep('no usable target margin');
    const price = round2(priceFromMargin(cost, m));
    return {
      price, changed: !approxEq(price, current),
      reason: `cost ${fmtMoney(cost)} ÷ (1 − ${(m * 100).toFixed(1)}%)`, source, basis: m,
    };
  }
  if (source === 'markup') {
    const cost = lineUnitCost(line, bd);
    const lineBasis = num(line && line.price_basis, NaN);
    const k = Number.isFinite(lineBasis) ? lineBasis : num(h.material_markup, NaN);
    if (!Number.isFinite(cost)) return keep('no cost to mark up');
    if (!Number.isFinite(k)) return keep('no usable markup');
    const price = round2(priceFromMarkup(cost, k));
    return {
      price, changed: !approxEq(price, current),
      reason: `cost ${fmtMoney(cost)} × (1 + ${(k * 100).toFixed(1)}%)`, source, basis: k,
    };
  }
  if (source === 'book') return keep('book price kept');
  if (source === 'override_member') return keep('priced by assembly override');
  return keep('manual price kept');
}

/**
 * Legacy lines (saved before price_source existed): infer the basis ONCE from
 * the number that was stored. Tolerance is half a cent plus float slack so a
 * price stored as toFixed(2) still matches its unrounded formula
 * (252.13 vs 151.28 / 0.6 = 252.1333).
 */
export function inferPriceSource(line, breakdown, header) {
  if (isTmPricedLine(line, header)) return { price_source: 'tm_pending', price_basis: null, inferred: true, reason: TM_REASON };
  const tol = 0.0051;
  const current = num(line && line.price, NaN);
  const bd = breakdown || (line && line.cost_breakdown) || null;
  const h = header || {};
  const out = (price_source, price_basis, reason) => ({ price_source, price_basis, inferred: true, reason });

  if (!Number.isFinite(current) || current === 0) return out('calculated', null, 'unpriced line');

  if (line && line.is_assembly_ops) {
    const calc = num(bd && bd.calculated_price, NaN);
    if (Number.isFinite(calc) && approxEq(calc, current, tol)) return out('calculated', null, 'matches calculated price');
    return out('manual', null, 'does not match the calculated price');
  }

  if (line && line.is_consumable_view) {
    const book = num(line._book_price, NaN);
    if (book > 0 && approxEq(book, current, tol)) return out('book', null, 'matches book price');
    const cost = lineUnitCost(line, bd);
    const k = num(h.material_markup, NaN);
    if (Number.isFinite(cost) && cost > 0 && Number.isFinite(k) && approxEq(cost * (1 + k), current, tol)) {
      return out('markup', k, 'matches cost × (1 + markup)');
    }
    const m = num(h.target_margin, NaN);
    if (Number.isFinite(cost) && cost > 0 && Number.isFinite(m) && m < 1 && approxEq(cost / (1 - m), current, tol)) {
      return out('target_margin', m, 'matches cost ÷ (1 − margin)');
    }
    return out('manual', null, 'matches neither book nor markup');
  }

  const calc = num(bd && bd.calculated_price, NaN);
  if (Number.isFinite(calc) && approxEq(calc, current, tol)) return out('calculated', null, 'matches calculated price');
  const cost = num(bd && bd.total_cost, NaN);
  if (Number.isFinite(cost) && cost > 0) {
    const candidates = [num(h.target_margin, NaN), 0.4]
      .filter((m, i, arr) => Number.isFinite(m) && m < 1 && arr.indexOf(m) === i);
    for (const m of candidates) {
      if (approxEq(cost / (1 - m), current, tol)) return out('target_margin', m, `matches ${(m * 100).toFixed(0)}% target on cost`);
    }
  }
  const book = num(line && line.current_db_price, NaN);
  if (book > 0 && approxEq(book, current, tol)) return out('book', null, 'matches book price');
  return out('manual', null, 'matches no known basis');
}

/**
 * Drift between the line's price and what its basis says it should be.
 * manual/book lines: compares to the CALCULATED price (Apply switches basis).
 * calculated/target/markup lines: compares to the re-derived price.
 * null when nothing to show.
 */
export function priceDrift(line, header) {
  if (isTmPricedLine(line, header)) return null;
  if (!line || !line.cost_breakdown) return null;
  const current = num(line.price, NaN);
  const source = effectivePriceSource(line);
  if (source === 'manual' || source === 'book' || source === 'override_member') {
    const calc = num(line.cost_breakdown.calculated_price, NaN);
    if (!Number.isFinite(calc) || !Number.isFinite(current)) return null;
    const price = round2(calc);
    if (approxEq(price, current)) return null;
    return {
      kind: 'manual_vs_calc', calculated: price, price: current, delta: current - price,
      apply: { price, price_source: 'calculated', price_basis: null },
    };
  }
  const d = deriveLinePrice(line, line.cost_breakdown, header);
  if (!d.changed || d.price == null) return null;
  return {
    kind: 'basis_drift', calculated: d.price, price: Number.isFinite(current) ? current : 0,
    delta: (Number.isFinite(current) ? current : 0) - d.price,
    apply: { price: d.price, price_source: source, price_basis: d.basis },
  };
}

/**
 * Set count for an assembly group: primary line qty ÷ ppq; else the ops line
 * quantity (it is per set); else 1 — and SAY which (F39).
 */
export function computeSetCount(lines, asmId, asmDef) {
  const group = (lines || []).filter(l => String(l._source_assembly_id) === String(asmId));
  const ppq = num(asmDef && asmDef.primary_part_quantity, 0) || 1;
  const primaryPartId = asmDef && asmDef.primary_part_id;
  const primary = primaryPartId != null
    ? group.find(l => l.part_id === primaryPartId && !l.is_assembly_ops && !l.is_consumable_view)
    : null;
  if (primary) {
    const qty = parseInt(primary.quantity, 10) || 0;
    return { setCount: Math.floor(qty / ppq), source: 'primary', ppq, primaryLine: primary };
  }
  const ops = group.find(l => l.is_assembly_ops);
  if (ops) {
    const qty = parseInt(ops.quantity, 10);
    return { setCount: Number.isFinite(qty) && qty > 0 ? qty : 1, source: 'ops', ppq, primaryLine: null };
  }
  return { setCount: 1, source: 'fallback', ppq, primaryLine: null };
}

const lineKind = (line) => (line.is_assembly_ops ? 'ops' : line.is_consumable_view ? 'purchased' : 'manufactured');

/**
 * Per-set cost / quoted price / calculated price for one assembly group,
 * split into Manufactured / Purchased / Operations (F20, F10, F39, F40).
 */
export function summarizeAssemblyGroup(lines, asmId, asmDef, { override, header } = {}) {
  const group = (lines || []).filter(l => String(l._source_assembly_id) === String(asmId));
  const sc = computeSetCount(lines, asmId, asmDef);
  const setCount = sc.setCount;
  const div = setCount > 0 ? setCount : 0;
  const mk = () => ({ cost: 0, price: 0, calc: 0, lines: 0, costKnown: 0, calcKnown: 0 });
  const buckets = { manufactured: mk(), purchased: mk(), ops: mk() };
  const missingCost = [];
  const missingCalc = [];
  const counts = { parts: 0, components: 0, ops: 0 };

  group.forEach(line => {
    const kind = lineKind(line);
    const b = buckets[kind];
    const qty = num(line.quantity, 0);
    const price = num(line.price, 0);
    const cost = lineUnitCost(line);
    const calc = lineCalculatedPrice(line, null, header);
    b.lines += 1;
    b.price += price * qty;
    if (Number.isFinite(cost)) { b.cost += cost * qty; b.costKnown += 1; } else missingCost.push(line.product_id || line.description || 'line');
    if (Number.isFinite(calc)) { b.calc += calc * qty; b.calcKnown += 1; } else missingCalc.push(line.product_id || line.description || 'line');
    if (kind === 'ops') counts.ops += 1; else if (kind === 'purchased') counts.components += 1; else counts.parts += 1;
  });

  const perSet = (v) => (div > 0 ? v / div : 0);
  const rows = ['manufactured', 'purchased', 'ops'].map(key => ({
    key,
    label: key === 'manufactured' ? 'Manufactured parts' : key === 'purchased' ? 'Purchased components' : 'Assembly operations',
    lines: buckets[key].lines,
    cost: perSet(buckets[key].cost), price: perSet(buckets[key].price), calc: perSet(buckets[key].calc),
    costKnown: buckets[key].costKnown, calcKnown: buckets[key].calcKnown,
    margin: buckets[key].price > 0 ? marginPct(buckets[key].cost, buckets[key].price) : null,
  }));
  const totalCost = perSet(buckets.manufactured.cost + buckets.purchased.cost + buckets.ops.cost);
  const totalPrice = perSet(buckets.manufactured.price + buckets.purchased.price + buckets.ops.price);
  const totalCalc = perSet(buckets.manufactured.calc + buckets.purchased.calc + buckets.ops.calc);
  const ov = num(override, NaN);
  const hasOverride = override !== undefined && override !== null && override !== '' && Number.isFinite(ov);
  const effectivePerSet = hasOverride ? ov : totalPrice;
  const sys = asmDef || {};
  const sysVal = num(sys.assembly_price, NaN);

  return {
    asmId: String(asmId),
    name: (asmDef && asmDef.assembly_name) || (group[0] && group[0]._source_assembly_name) || `#${asmId}`,
    retired: !!asmDef && asmDef.active_status === false,
    unknownAssembly: !asmDef,
    setCount, setCountSource: sc.source, ppq: sc.ppq,
    counts, rows,
    total: {
      cost: totalCost, price: totalPrice, calc: totalCalc,
      margin: totalPrice > 0 && missingCost.length === 0 ? marginPct(totalCost, totalPrice) : null,
      calcMargin: totalCalc > 0 && missingCost.length === 0 ? marginPct(totalCost, totalCalc) : null,
    },
    costComplete: missingCost.length === 0, missingCost, missingCalc,
    drift: totalPrice - totalCalc,
    hasOverride, override: hasOverride ? ov : null,
    effectivePerSet, effectiveTotal: effectivePerSet * setCount,
    overrideDelta: hasOverride ? ov - totalPrice : null,
    // Keep the field names the rest of the page already reads.
    componentTotal: buckets.manufactured.price + buckets.purchased.price + buckets.ops.price,
    unitComponentPrice: totalPrice,
    systemPrice: Number.isFinite(sysVal) && sysVal > 0 ? sysVal : null,
    systemPriceSource: sys.assembly_price_source || null,
    systemPriceQuoteId: sys.assembly_price_quote_id || null,
    systemPriceSetAt: sys.assembly_price_set_at || null,
  };
}

/** Quote subtotal: Σ qty × price, with overridden assemblies billed as override × sets. */
export function computeSubtotal(lines, overrides, assemblies) {
  const ov = overrides || {};
  const overridden = new Set(Object.keys(ov).filter(k => ov[k] !== '' && ov[k] != null).map(String));
  const counted = new Set();
  let total = 0;
  (lines || []).forEach(line => {
    const asmId = line._source_assembly_id != null ? String(line._source_assembly_id) : null;
    if (asmId && overridden.has(asmId)) {
      if (!counted.has(asmId)) {
        const asmDef = (assemblies || []).find(a => String(a.assembly_id) === asmId);
        total += num(ov[asmId]) * computeSetCount(lines, asmId, asmDef).setCount;
        counted.add(asmId);
      }
      return;
    }
    total += num(line.quantity) * num(line.price);
  });
  return total;
}

/**
 * Why the engine could not work out this line's material cost: [{code, message}], empty when
 * it is known (jobq-api cost propagation step 1).
 *  - `breakdown.material_unresolved`: the engine said so when it priced the line.
 *  - `line.stale.material_unresolved` with a stored material of $0: an older engine priced the
 *    line after its cut sheet row had gone, so that $0 is not a cost either. (A snapshot that
 *    stored a real material cost before the row went missing keeps it; the badge still shows.)
 * `breakdown` (optional) is a fresh breakdown not yet on the line; the line's stale verdict is
 * about its OWN stored snapshot, so it is used only when `breakdown` is that snapshot.
 */
export function materialUnresolved(line, breakdown) {
  const bd = breakdown || (line && line.cost_breakdown) || null;
  const own = bd && Array.isArray(bd.material_unresolved) ? bd.material_unresolved.filter(Boolean) : [];
  if (own.length) return own;
  const st = line && line.stale;
  if (bd && line && bd === line.cost_breakdown && st && Array.isArray(st.material_unresolved)
      && st.material_unresolved.length && num(st.material_stored, NaN) === 0) {
    return st.material_unresolved.filter(Boolean);
  }
  return [];
}

/** A unit cost as the quote books it: 4 dp, what jobq-api's cost_subtotal and the SO's unit_cost_snapshot use. */
const round4 = (v) => Math.round(v * 10000) / 10000;

/**
 * Classify one line's per-unit COST into the five Financial Summary buckets
 * (F38). Outsourced = vendor / no-minutes operation rows and Coating rows; the
 * `coating` block counts only when it is not a summary of those rows
 * (coatingBlockIsSummary). `other` is whatever the engine's total_cost
 * carries that the buckets could not name — shown so Σ buckets = total.
 *
 * A breakdown that carries `total_cost_exact` (engine bench D02) is read from
 * its unrounded twins (`cost_exact`, `*_cost_exact`): the engine rounds each
 * row to the cent but the total once, so the 2-dp rows fell a cent or so short
 * of it per unit and "Not itemised by the engine" showed that rounding × qty
 * (Quote 353, 2026-09-30: $0.05). The line's cost is then
 * round4(total_cost_exact) — jobq-api's own cost_subtotal rule — and the gap
 * between that and the unrounded parts is `rounding`, never `other`. Older
 * 2-dp breakdowns keep the old reading.
 */
export function classifyLineCost(line) {
  const z = { material: 0, purchased: 0, outsourced: 0, labour: 0, gasCons: 0, other: 0, rounding: 0, total: 0 };
  if (!line) return { ...z, unresolved: false, noBreakdown: true, kind: 'none' };
  const kind = lineKind(line);
  const bd = line.cost_breakdown && line.cost_breakdown.breakdown ? line.cost_breakdown.breakdown : null;
  const totalExact = num(line.cost_breakdown && line.cost_breakdown.total_cost_exact, NaN);
  const exact = Number.isFinite(totalExact);
  // A row's cost: its unrounded twin when this breakdown has them, else the 2-dp figure.
  const costOf = (obj, key = 'cost') => {
    if (!obj) return 0;
    const e = exact ? num(obj[`${key}_exact`], NaN) : NaN;
    return Number.isFinite(e) ? e : num(obj[key], 0);
  };

  if (kind === 'purchased') {
    const cost = exact ? round4(totalExact) : lineUnitCost(line);
    if (!Number.isFinite(cost)) return { ...z, unresolved: true, noBreakdown: !line.cost_breakdown, kind };
    return { ...z, purchased: cost, total: cost, unresolved: false, noBreakdown: !line.cost_breakdown, kind };
  }

  if (!bd) {
    // Manual / fallback: only a typed material price is known.
    const mat = line.is_customer_supplied_material ? 0 : num(line.manual_material_price, 0);
    return { ...z, material: mat, total: mat, unresolved: false, noBreakdown: true, kind };
  }

  // materialUnknown: the engine could not work out the material (a cut sheet row that no longer
  // exists, no vendor price…). Its $0.00 is not a cost of $0: the other buckets still count, and
  // the Financial Summary names the line (cost understated, margin overstated until it is fixed).
  const out = { ...z, unresolved: false, noBreakdown: false, kind, materialUnknown: materialUnresolved(line).length > 0 };
  const engineMaterial = costOf(bd.material);
  out.material = line.is_customer_supplied_material ? 0 : engineMaterial;

  const laser = bd.laser;
  if (laser) {
    const d = laser.details || {};
    const lx = [num(d.labor_cost_exact, NaN), num(d.gas_cost_exact, NaN), num(d.consumable_cost_exact, NaN)];
    if (exact && lx.every(Number.isFinite)) {
      // the three unrounded laser parts (review F2); never mixed with the 2-dp laser.total_cost
      out.labour += lx[0];
      out.gasCons += lx[1] + lx[2];
    } else {
      const gas = num(d.gas_cost, 0);
      const cons = num(d.consumable_cost, 0);
      const laserTotal = num(laser.total_cost, NaN);
      const labour = Number.isFinite(laserTotal)
        ? laserTotal - gas - cons
        : (num(d.cutting_cost, 0) + num(d.shuttle_cost, 0)) || num(d.labor_cost, 0);
      out.labour += labour;
      out.gasCons += gas + cons;
    }
  }
  (bd.operations || []).forEach(op => {
    if (!op) return;
    const cost = costOf(op);
    if (isOutsourcedOp(op)) out.outsourced += cost; else out.labour += cost;
  });
  if (bd.coating && num(bd.coating.cost, 0) > 0 && !coatingBlockIsSummary(bd)) out.outsourced += num(bd.coating.cost, 0);

  const named = out.material + out.labour + out.gasCons + out.outsourced;
  if (exact) {
    // CSM: the engine may still have priced material; it is zeroed above and taken off here too (as the server does).
    const base = totalExact - (line.is_customer_supplied_material ? engineMaterial : 0);
    const rest = base - named;
    out.other = Math.abs(rest) > 1e-6 ? rest : 0;           // an engine cost no row names
    out.rounding = round4(base) - (named + out.other);       // what booking the unit cost at 4 dp moves it by
    out.total = named + out.other + out.rounding;            // = round4(base), the server's unit cost
    return out;
  }
  const total = num(line.cost_breakdown.total_cost, NaN);
  if (Number.isFinite(total)) {
    const rest = total - named - (line.is_customer_supplied_material ? num(bd.material && bd.material.cost, 0) : 0);
    // CSM: the engine may still have priced material; we zero it (existing
    // behaviour) and do not let that gap reappear as "other".
    out.other = Math.abs(rest) > 0.005 ? rest : 0;
    out.total = named + out.other;
  } else {
    out.total = named;
  }
  return out;
}

/**
 * Where an outsourced row's markup came from (F43, wave 2). The engine stamps
 * `markup_source` on every outsourced / vendor operation row and on
 * breakdown.coating; breakdowns stored before then carry none (→ null).
 */
export const MARKUP_SOURCE_LABELS = Object.freeze({
  quote_coating_markup: { label: 'quote outsourced markup', title: "Markup from this quote's Outsourced markup % (Quote Setup)." },
  process_default_markup: { label: 'process default markup', title: "The quote's Outsourced markup % is blank, so the process's own default markup applied." },
  material_markup: { label: 'material markup', title: "No outsourced or process markup was set, so the quote's Markup % (material markup) applied." },
});

/** {key, label, title} for a markup_source value; unknown values pass through readable; blank → null. */
export function markupSourceLabel(source) {
  if (!source) return null;
  const known = MARKUP_SOURCE_LABELS[source];
  if (known) return { key: source, ...known };
  return { key: source, label: String(source).replace(/_/g, ' '), title: `Markup source reported by the engine: ${source}` };
}

/** An operations row is outsourced when the engine says so, or (legacy rows) when it is Coating. */
export function isOutsourcedOp(op) {
  if (!op) return false;
  if (op.is_outsourced === true || op.source === 'vendor') return true;
  if (Object.prototype.hasOwnProperty.call(op, 'minutes') && op.minutes == null && op.source !== 'historical' && op.source !== 'estimate' && op.source !== 'assembly_row') return true;
  if (op.source == null && op.station === 'Coating') return true;
  return false;
}

/**
 * True when the engine's `breakdown.coating` block summarises vendor rows it
 * also wrote into `operations`. Both engine paths (the part line and the
 * assembly-ops line) write each outsourced step as an operations row AND sum
 * the same steps into the block; total_cost counts each step once, and
 * jobq-api's own SO costing reads only the rows. Adding the block on top
 * counted coating twice and drove "Not itemised" negative by the same amount
 * (Quote 349, 2026-09-29: Outsourced $4,620 for $2,310 of coating). A block
 * with no outsourced row beside it is still counted on its own.
 */
export function coatingBlockIsSummary(bd) {
  if (!bd || !bd.coating) return false;
  return (bd.operations || []).some(op => op && isOutsourcedOp(op) && num(op.cost, 0) > 0);
}

/**
 * Financial Performance Summary (F38/F41): revenue (overrides honoured) and
 * the cost buckets summed over qty, plus the lines whose cost is unknown.
 */
export function financialBuckets(lines, overrides, assemblies) {
  const ov = overrides || {};
  const overridden = new Set(Object.keys(ov).filter(k => ov[k] !== '' && ov[k] != null).map(String));
  const counted = new Set();
  const buckets = { material: 0, purchased: 0, outsourced: 0, labour: 0, gasCons: 0, other: 0, rounding: 0 };
  const unresolved = [];
  const noBreakdown = [];
  const materialUnknown = [];
  let revenue = 0;
  let lineCost = 0;   // Σ line unit cost × qty, summed the way jobq-api's cost_subtotal is (same float, same cent)

  (lines || []).forEach(line => {
    const qty = num(line.quantity, 0);
    const asmId = line._source_assembly_id != null ? String(line._source_assembly_id) : null;
    if (asmId && overridden.has(asmId)) {
      if (!counted.has(asmId)) {
        const asmDef = (assemblies || []).find(a => String(a.assembly_id) === asmId);
        revenue += num(ov[asmId]) * computeSetCount(lines, asmId, asmDef).setCount;
        counted.add(asmId);
      }
    } else {
      revenue += num(line.price) * qty;
    }
    const c = classifyLineCost(line);
    const label = line.product_id || line.description || 'line';
    if (c.unresolved) { unresolved.push(label); return; }
    if (c.noBreakdown && c.kind === 'manufactured' && (line.part_id || line.product_id)) noBreakdown.push(label);
    if (c.materialUnknown) materialUnknown.push(label);
    buckets.material += c.material * qty;
    buckets.purchased += c.purchased * qty;
    buckets.outsourced += c.outsourced * qty;
    buckets.labour += c.labour * qty;
    buckets.gasCons += c.gasCons * qty;
    buckets.other += c.other * qty;
    buckets.rounding += c.rounding * qty;
    lineCost += c.total * qty;
  });

  // = Σ buckets (each line's total is its buckets), taken the way jobq-api's cost_subtotal is:
  // round(Σ unit × qty, 2). toFixed rounds the float's exact value, as Python's round() does, so a
  // half cent lands where the server's does (Quote 346: 99.8039 × 50 = 4,990.194999… → $4,990.19;
  // Intl's shortest-decimal rounding would print $4,990.20 beside the server's $4,990.19).
  const totalCost = Number(lineCost.toFixed(2));
  const grossProfit = revenue - totalCost;
  return {
    revenue, buckets, totalCost, grossProfit,
    margin: revenue > 0 ? grossProfit / revenue : null,
    unresolved, noBreakdown, materialUnknown,
  };
}

/** Rows for the Recalculate-All preview modal: one per line, old → new. */
export function recalcPreviewRows(oldLines, newLines) {
  const byId = new Map((oldLines || []).map(l => [l.id, l]));
  return (newLines || []).map((nl, i) => {
    const old = byId.get(nl.id) || (oldLines || [])[i] || {};
    const oldPrice = num(old.price, 0);
    const newPrice = num(nl.price, 0);
    const qty = num(nl.quantity, 0);
    return {
      id: nl.id,
      label: nl.product_id || nl.description || `line ${i + 1}`,
      kind: lineKind(nl),
      basis: effectivePriceSource(nl),
      basisValue: nl.price_basis != null ? num(nl.price_basis, null) : null,
      qty, oldPrice, newPrice, delta: newPrice - oldPrice,
      oldExt: oldPrice * qty, newExt: newPrice * qty,
      breakdownRefreshed: !!nl.cost_breakdown && nl.cost_breakdown !== old.cost_breakdown,
      materialUnresolved: materialUnresolved(nl).length > 0,
      note: nl._recalc_note || (materialUnresolved(nl).length
        ? `material cost unresolved: ${materialUnresolved(nl)[0].message || materialUnresolved(nl)[0].code} (price kept)` : null),
      changed: !approxEq(oldPrice, newPrice),
    };
  });
}

/**
 * Normalise a stored breakdown into Cost Analysis rows (F08/F37). Each labour
 * row carries minutes, quoted $/hr → price and true $/hr → cost so the
 * operator can multiply them back. Rates missing from legacy breakdowns are
 * IMPLIED from price ÷ hours (and flagged) when minutes are known; when even
 * minutes are missing the row says so instead of inventing numbers.
 */
export function costAnalysisRows(breakdown, { isOps = false } = {}) {
  const inputs = (breakdown && breakdown.inputs) || null;
  const rates = (inputs && inputs.rates) || {};
  const bd = breakdown && breakdown.breakdown ? breakdown.breakdown : null;
  const totalCost = num(breakdown && breakdown.total_cost, NaN);
  const calculatedPrice = num(breakdown && breakdown.calculated_price, NaN);
  // The engine reports profit_margin as a PERCENT (79.0); older/other callers
  // may store a fraction. Prefer re-deriving from the totals (always a
  // fraction); fall back to the stored value normalised to a fraction.
  const rawMargin = num(breakdown && breakdown.profit_margin, NaN);
  const profitMargin = Number.isFinite(totalCost) && Number.isFinite(calculatedPrice) && calculatedPrice !== 0
    ? (calculatedPrice - totalCost) / calculatedPrice
    : (Number.isFinite(rawMargin) ? (Math.abs(rawMargin) > 1 ? rawMargin / 100 : rawMargin) : NaN);
  const base = {
    rows: [], inputs, hasProvenance: !!inputs,
    provenanceNote: inputs ? null : 'n/a — priced before provenance was recorded',
    excludedSteps: (bd && Array.isArray(bd.excluded_steps)) ? bd.excluded_steps : [],
    totalCost,
    calculatedPrice,
    profitMargin,
    log: (breakdown && Array.isArray(breakdown.log)) ? breakdown.log : null,
  };
  if (!bd) return base;
  const rows = base.rows;

  const labourRow = (partial) => {
    const minutes = num(partial.minutes, NaN);
    const hours = Number.isFinite(minutes) ? minutes / 60 : NaN;
    const price = num(partial.price, NaN);
    const cost = num(partial.cost, NaN);
    let quotedRate = num(partial.quotedRate, NaN);
    let trueRate = num(partial.trueRate, NaN);
    let impliedQuoted = false;
    let impliedTrue = false;
    if (!Number.isFinite(quotedRate) && hours > 0 && Number.isFinite(price)) { quotedRate = price / hours; impliedQuoted = true; }
    if (!Number.isFinite(trueRate) && hours > 0 && Number.isFinite(cost)) { trueRate = cost / hours; impliedTrue = true; }
    return {
      ...partial,
      kind: 'labour', minutes, hours, price, cost, quotedRate, trueRate, impliedQuoted, impliedTrue,
      source: partial.source || null, historyN: partial.historyN != null ? num(partial.historyN) : null,
      trueRateSource: partial.trueRateSource || null,
      isDefaultRate: partial.trueRateSource === 'default',
      outsourced: false, markup: null,
    };
  };

  if (!isOps && bd.material) {
    const m = bd.material;
    const src = m.source || null;
    rows.push({
      key: 'material', kind: 'material', label: 'Material',
      cost: num(m.cost, NaN), price: num(m.price, NaN),
      method: m.method || null,
      markup: m.markup != null ? num(m.markup, NaN) : (inputs && inputs.material_markup != null ? num(inputs.material_markup, NaN) : NaN),
      sourceKind: src ? src.kind || null : null,
      description: src ? src.description || null : null,
      utilisation: src && src.utilisation_pct != null ? num(src.utilisation_pct, NaN) : NaN,
      source: src,
    });
  }

  const laser = bd.laser;
  if (laser && (num(laser.quoted_price, 0) > 0 || num(laser.total_cost, 0) > 0 || num(laser.details && laser.details.cutting_hours, 0) > 0)) {
    const d = laser.details || {};
    const gas = num(d.gas_cost, 0);
    const cons = num(d.consumable_cost, 0);
    const hasShuttle = num(d.shuttle_cost, 0) > 0;
    const quotedRate = firstFinite(laser.quoted_rate, rates.laser_rate);
    const trueRate = firstFinite(laser.true_rate);
    const cuttingPrice = firstFinite(d.cutting_price, num(laser.quoted_price, NaN) - gas - cons - (hasShuttle ? num(d.shuttle_price, 0) : 0));
    const cuttingCost = firstFinite(d.cutting_cost, d.labor_cost);
    rows.push(labourRow({
      key: 'laser_cut', station: 'Laser', label: hasShuttle ? 'Laser cutting' : 'Laser (operator)',
      minutes: num(d.cutting_hours, NaN) * 60, quotedRate, trueRate,
      price: cuttingPrice, cost: cuttingCost,
      priceDerived: !Number.isFinite(num(d.cutting_price, NaN)),
      source: laser.hours_source || null, trueRateSource: laser.true_rate_source || null,
    }));
    if (hasShuttle) {
      rows.push(labourRow({
        key: 'laser_shuttle', station: 'Laser', label: 'Shuttle overhead',
        minutes: num(d.shuttle_hours, NaN) * 60, quotedRate, trueRate,
        price: num(d.shuttle_price, NaN), cost: num(d.shuttle_cost, NaN),
        source: laser.shuttle_included === false ? null : 'estimate', trueRateSource: laser.true_rate_source || null,
      }));
    }
    rows.push({ key: 'laser_gas', kind: 'passthrough', label: 'Gas', cost: gas, price: gas, source: laser.gas_source || null, precise: false });
    rows.push({ key: 'laser_cons', kind: 'passthrough', label: 'Consumables (nozzle / lens)', cost: cons, price: cons, source: null, precise: true });
    rows.push({ key: 'laser_sub', kind: 'subtotal', label: 'Laser subtotal', cost: num(laser.total_cost, NaN), price: num(laser.quoted_price, NaN) });
  }

  // A summary block is not a second charge: its owner rides on the vendor rows instead.
  const blockIsSummary = coatingBlockIsSummary(bd);
  const summaryOwner = blockIsSummary ? (bd.coating.owner || null) : null;

  (bd.operations || []).forEach((op, i) => {
    if (!op) return;
    const outsourced = isOutsourcedOp(op);
    const label = op.sub_process && op.sub_process !== op.station ? `${op.station} · ${op.sub_process}` : (op.station || `Operation ${i + 1}`);
    if (outsourced) {
      const cost = num(op.cost, NaN);
      const price = num(op.price, NaN);
      let markup = op.markup != null ? num(op.markup, NaN) : NaN;
      let impliedMarkup = false;
      if (!Number.isFinite(markup) && cost > 0 && Number.isFinite(price)) { markup = price / cost - 1; impliedMarkup = true; }
      rows.push({
        key: `op_${i}`, kind: 'outsourced', label, station: op.station || null,
        cost, price, markup, impliedMarkup, source: op.source || 'vendor', outsourced: true,
        markupSource: op.markup_source || null, owner: summaryOwner,
      });
      return;
    }
    rows.push(labourRow({
      key: `op_${i}`, station: op.station || null, label,
      minutes: op.minutes, quotedRate: op.quoted_rate, trueRate: op.true_rate,
      price: op.price, cost: op.cost, source: op.source || null, historyN: op.history_n,
      trueRateSource: op.true_rate_source || null,
    }));
  });

  if (bd.coating && !blockIsSummary && (num(bd.coating.cost, 0) > 0 || num(bd.coating.price, 0) > 0)) {
    const c = bd.coating;
    const cost = num(c.cost, NaN);
    const price = num(c.price, NaN);
    let markup = c.markup != null ? num(c.markup, NaN) : NaN;
    let impliedMarkup = false;
    if (!Number.isFinite(markup) && cost > 0 && Number.isFinite(price)) { markup = price / cost - 1; impliedMarkup = true; }
    rows.push({
      key: 'coating', kind: 'outsourced', label: 'Coating (part-level)', station: 'Coating', cost, price, markup, impliedMarkup,
      source: 'vendor', outsourced: true, owner: c.owner || 'part', markupSource: c.markup_source || null,
    });
  }

  return base;
}

/** Human basis label incl. the basis fraction, e.g. "Target margin 75%". */
export function basisLabel(line) {
  const s = effectivePriceSource(line);
  const b = num(line && line.price_basis, NaN);
  switch (s) {
    case 'calculated': return 'Calculated';
    case 'target_margin': return Number.isFinite(b) ? `Target margin ${(b * 100).toFixed(b * 100 % 1 === 0 ? 0 : 1)}%` : 'Target margin';
    case 'markup': return Number.isFinite(b) ? `Cost + ${(b * 100).toFixed(b * 100 % 1 === 0 ? 0 : 1)}%` : 'Cost + markup';
    case 'book': return 'Book price';
    case 'manual': return 'Manual';
    case 'override_member': return 'In override';
    case 'ops': return 'Assembly ops';
    case 'tm_pending': return 'T&M (priced at completion)';
    default: return s;
  }
}
